// Spec 47 approval-ux — diff_block_before_after + diff_block_unified.
//
// Two diff renderers consolidated under one component with a `variant`
// discriminator:
//
//   * before_after — side-by-side <table> with field/before/after rows
//     + delta chips. Spec 47 line 366 says: "Used for any structured
//     change: comp adjustments, HRIS attribute updates, bulk AI-staged
//     changes...".
//   * unified — inline <ins>/<del> diff for prose. Spec 47 line 422:
//     "Inline unified diff for prose content: offer letter bodies,
//     policy language, document templates...".
//
// a11y per spec:
//   before_after: <table> + <thead> + <tbody> + row scope; delta chips
//     have aria-label spelling out the full change; changed rows carry
//     aria-label='changed'.
//   unified: <ins> and <del> for semantic correctness; color alone
//     never signals addition/removal (strikethrough + ins/del semantics
//     carry meaning).

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

// ==========================================================================
// BEFORE-AFTER (SIDE-BY-SIDE) VARIANT
// ==========================================================================

export interface DiffBlockBeforeAfterRow {
  readonly fieldKey: string;
  /** Human-readable field name — "Base salary", not "base_salary_minor_units". */
  readonly fieldLabel: string;
  /** Display value as a string — caller formats per spec 02 money_convention etc. */
  readonly before: string;
  readonly after: string;
  /** Delta chip text + a11y. Omit when no delta (unchanged row). */
  readonly delta?: { readonly display: string; readonly ariaLabel: string };
  /** True when this row represents a change (vs. unchanged context row). */
  readonly changed: boolean;
}

export interface DiffBlockBeforeAfterProps extends HTMLAttributes<HTMLTableElement> {
  readonly variant: "before_after";
  readonly rows: readonly DiffBlockBeforeAfterRow[];
  readonly fieldColumnLabel?: string;
  readonly beforeColumnLabel?: string;
  readonly afterColumnLabel?: string;
  /** Optional "Showing N of M changed fields" caption when filtered. */
  readonly rowCountSummary?: string;
}

// ==========================================================================
// UNIFIED (INLINE) VARIANT
// ==========================================================================

export type DiffBlockUnifiedSegment =
  | { readonly kind: "unchanged"; readonly text: string }
  | { readonly kind: "addition"; readonly text: string }
  | { readonly kind: "removal"; readonly text: string };

export interface DiffBlockUnifiedProps extends HTMLAttributes<HTMLDivElement> {
  readonly variant: "unified";
  readonly segments: readonly DiffBlockUnifiedSegment[];
  readonly documentTitle?: string;
}

// ==========================================================================
// DISCRIMINATED-UNION ROOT
// ==========================================================================

export type DiffBlockProps = DiffBlockBeforeAfterProps | DiffBlockUnifiedProps;

export const DiffBlock = forwardRef<HTMLElement, DiffBlockProps>((props, ref) => {
  if (props.variant === "before_after") {
    return <DiffBlockBeforeAfter {...props} ref={ref as React.Ref<HTMLTableElement>} />;
  }
  return <DiffBlockUnified {...props} ref={ref as React.Ref<HTMLDivElement>} />;
});
DiffBlock.displayName = "DiffBlock";

// ==========================================================================
// IMPLEMENTATION — BEFORE-AFTER
// ==========================================================================

const DiffBlockBeforeAfter = forwardRef<HTMLTableElement, DiffBlockBeforeAfterProps>(
  (
    {
      rows,
      fieldColumnLabel = "Field",
      beforeColumnLabel = "Before",
      afterColumnLabel = "After",
      rowCountSummary,
      className,
      variant: _variant,
      ...rest
    },
    ref,
  ) => {
    return (
      <div data-component="diff-block-wrapper" className="nai-diff-block-wrapper">
        {rowCountSummary !== undefined && (
          <div
            data-component="diff-block-row-count"
            className="nai-diff-block-row-count"
          >
            {rowCountSummary}
          </div>
        )}
        <table
          ref={ref}
          data-component="diff-block"
          data-variant="before_after"
          className={cx("nai-diff-block", "nai-diff-block-before-after", className)}
          {...rest}
        >
          <thead>
            <tr>
              <th scope="col">{fieldColumnLabel}</th>
              <th scope="col">{beforeColumnLabel}</th>
              <th scope="col">{afterColumnLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.fieldKey}
                data-changed={row.changed || undefined}
                aria-label={row.changed ? "changed" : undefined}
              >
                <th scope="row" className="nai-diff-block-row-label">
                  {row.fieldLabel}
                </th>
                <td className="nai-diff-block-before">{row.before}</td>
                <td className="nai-diff-block-after">
                  <span>{row.after}</span>
                  {row.delta !== undefined && (
                    <span
                      data-component="diff-block-delta"
                      className="nai-diff-block-delta"
                      aria-label={row.delta.ariaLabel}
                    >
                      {row.delta.display}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  },
);
DiffBlockBeforeAfter.displayName = "DiffBlock.BeforeAfter";

// ==========================================================================
// IMPLEMENTATION — UNIFIED
// ==========================================================================

const DiffBlockUnified = forwardRef<HTMLDivElement, DiffBlockUnifiedProps>(
  ({ segments, documentTitle, className, variant: _variant, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        data-component="diff-block"
        data-variant="unified"
        className={cx("nai-diff-block", "nai-diff-block-unified", className)}
        {...rest}
      >
        {documentTitle !== undefined && (
          <div data-component="diff-block-document-title" className="nai-diff-block-document-title">
            {documentTitle}
          </div>
        )}
        <div data-component="diff-block-prose" className="nai-diff-block-prose">
          {segments.map((seg, idx) => renderSegment(seg, idx))}
        </div>
      </div>
    );
  },
);
DiffBlockUnified.displayName = "DiffBlock.Unified";

function renderSegment(seg: DiffBlockUnifiedSegment, idx: number): ReactNode {
  if (seg.kind === "addition") {
    return (
      <ins
        key={idx}
        data-component="diff-block-segment-addition"
        className="nai-diff-block-segment-addition"
      >
        {seg.text}
      </ins>
    );
  }
  if (seg.kind === "removal") {
    return (
      <del
        key={idx}
        data-component="diff-block-segment-removal"
        className="nai-diff-block-segment-removal"
      >
        {seg.text}
      </del>
    );
  }
  return (
    <span
      key={idx}
      data-component="diff-block-segment-unchanged"
      className="nai-diff-block-segment-unchanged"
    >
      {seg.text}
    </span>
  );
}
